import { useState, type CSSProperties } from "react";
import { cn } from "../lib/utils";

interface TransferListProps {
  left?: string[];
  right?: string[];
  leftLabel?: string;
  rightLabel?: string;
  onChange?: (left: string[], right: string[]) => void;
  className?: string;
  style?: CSSProperties;
}

export function TransferList({
  left: initLeft = [], right: initRight = [],
  leftLabel = "Available", rightLabel = "Selected",
  onChange, className, style,
}: TransferListProps) {
  const [left, setLeft] = useState(initLeft);
  const [right, setRight] = useState(initRight);
  const [leftSel, setLeftSel] = useState<Set<string>>(new Set());
  const [rightSel, setRightSel] = useState<Set<string>>(new Set());

  function toggle(set: Set<string>, key: string) {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }

  function moveTo(dir: "right" | "left") {
    if (dir === "right") {
      const moving = [...leftSel];
      const newLeft  = left.filter(s => !leftSel.has(s));
      const newRight = [...right, ...moving];
      setLeft(newLeft); setRight(newRight); setLeftSel(new Set());
      onChange?.(newLeft, newRight);
    } else {
      const moving = [...rightSel];
      const newRight = right.filter(s => !rightSel.has(s));
      const newLeft  = [...left, ...moving];
      setRight(newRight); setLeft(newLeft); setRightSel(new Set());
      onChange?.(newLeft, newRight);
    }
  }

  function moveAll(dir: "right" | "left") {
    if (dir === "right") { const l: string[] = [], r = [...right, ...left]; setLeft(l); setRight(r); setLeftSel(new Set()); onChange?.(l, r); }
    else { const r: string[] = [], l = [...left, ...right]; setRight(r); setLeft(l); setRightSel(new Set()); onChange?.(l, r); }
  }

  function Panel({ items, selected, onToggle, label }: { items: string[]; selected: Set<string>; onToggle: (k: string) => void; label: string }) {
    return (
      <div className="tl-panel">
        <div className="tl-panel-header">
          <span className="tl-panel-label">{label}</span>
          <span className="tl-panel-count">{items.length}</span>
        </div>
        <ul className="tl-list" role="listbox" aria-multiselectable="true" aria-label={label}>
          {items.map(item => (
            <li
              key={item}
              role="option"
              aria-selected={selected.has(item)}
              className={cn("tl-item", selected.has(item) && "tl-item-selected")}
              onClick={() => onToggle(item)}
            >
              {item}
            </li>
          ))}
          {items.length === 0 && <li className="tl-empty">Empty</li>}
        </ul>
      </div>
    );
  }

  return (
    <div className={cn("tl-root", className)} style={style}>
      <Panel items={left} selected={leftSel} onToggle={k => setLeftSel(toggle(leftSel, k))} label={leftLabel} />
      <div className="tl-actions">
        <button type="button" className="tl-btn" onClick={() => moveAll("right")} title="Move all right" aria-label="Move all right">»</button>
        <button type="button" className="tl-btn" onClick={() => moveTo("right")} disabled={leftSel.size === 0} title="Move selected right" aria-label="Move selected right">›</button>
        <button type="button" className="tl-btn" onClick={() => moveTo("left")} disabled={rightSel.size === 0} title="Move selected left" aria-label="Move selected left">‹</button>
        <button type="button" className="tl-btn" onClick={() => moveAll("left")} title="Move all left" aria-label="Move all left">«</button>
      </div>
      <Panel items={right} selected={rightSel} onToggle={k => setRightSel(toggle(rightSel, k))} label={rightLabel} />
    </div>
  );
}
